/**
 * Процедурная пиксельная графика заставки: туман, облака, дальний лес,
 * вороны, молнии и «перекрашенные» варианты картинок (вспышка, силуэт).
 * Всё строится один раз при загрузке в маленьких канвасах — сеть не тратится.
 */

export type Canvas = HTMLCanvasElement;

export function makeCanvas(w: number, h: number): Canvas {
  const c = document.createElement("canvas");
  c.width = Math.max(1, Math.round(w));
  c.height = Math.max(1, Math.round(h));
  return c;
}

export function ctx2d(c: Canvas): CanvasRenderingContext2D {
  const g = c.getContext("2d", { willReadFrequently: false });
  if (!g) throw new Error("2d context");
  g.imageSmoothingEnabled = false;
  return g;
}

/** Детерминированный ГПСЧ (mulberry32) — сцена одинакова от запуска к запуску. */
export function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Упорядоченный дизеринг Байера 4×4: «ретро»-градиенты без полупрозрачности.
const BAYER4 = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5].map((v) => (v + 0.5) / 16);
export function bayer(x: number, y: number): number {
  return BAYER4[(y & 3) * 4 + (x & 3)];
}

/** Шум значений, периодичный по x (для бесшовной прокрутки). */
function valueNoise(w: number, h: number, cell: number, seed: number): Float32Array {
  const r = rng(seed);
  const gw = Math.max(1, Math.round(w / cell));
  const gh = Math.ceil(h / cell) + 2;
  const grid = new Float32Array(gw * gh);
  for (let i = 0; i < grid.length; i++) grid[i] = r();
  const out = new Float32Array(w * h);
  const sx = gw / w;
  for (let y = 0; y < h; y++) {
    const fy = y / cell;
    const y0 = Math.floor(fy);
    const ty = fy - y0;
    const vy = ty * ty * (3 - 2 * ty);
    for (let x = 0; x < w; x++) {
      const fx = x * sx;
      const x0 = Math.floor(fx);
      const tx = fx - x0;
      const vx = tx * tx * (3 - 2 * tx);
      const xa = x0 % gw;
      const xb = (x0 + 1) % gw;
      const a = grid[y0 * gw + xa];
      const b = grid[y0 * gw + xb];
      const c = grid[(y0 + 1) * gw + xa];
      const d = grid[(y0 + 1) * gw + xb];
      out[y * w + x] = (a + (b - a) * vx) * (1 - vy) + (c + (d - c) * vx) * vy;
    }
  }
  return out;
}

function fbm(w: number, h: number, cells: number[], seed: number): Float32Array {
  const out = new Float32Array(w * h);
  let amp = 1;
  let sum = 0;
  cells.forEach((cell, i) => {
    const n = valueNoise(w, h, cell, seed + i * 101);
    for (let k = 0; k < out.length; k++) out[k] += n[k] * amp;
    sum += amp;
    amp *= 0.5;
  });
  for (let k = 0; k < out.length; k++) out[k] /= sum;
  return out;
}

/**
 * Полоса тумана: плотность сверху вниз растёт, края рваные.
 * Пиксель либо закрашен (с фиксированной альфой), либо нет — без мыла.
 */
export function fogStrip(w: number, h: number, seed: number, rgb: [number, number, number], alpha: number): Canvas {
  const c = makeCanvas(w, h);
  const g = ctx2d(c);
  const img = g.createImageData(w, h);
  const n = fbm(w, h, [48, 20, 9], seed);
  for (let y = 0; y < h; y++) {
    const fall = Math.min(1, y / (h * 0.55)); // к низу плотнее
    const top = Math.min(1, (h - y) / (h * 0.25)); // нижний край тоже рваный
    for (let x = 0; x < w; x++) {
      const d = n[y * w + x] * 1.25 * fall * (0.55 + 0.45 * top) - 0.18;
      if (d > bayer(x, y)) {
        const i = (y * w + x) * 4;
        img.data[i] = rgb[0];
        img.data[i + 1] = rgb[1];
        img.data[i + 2] = rgb[2];
        img.data[i + 3] = Math.round(alpha * 255);
      }
    }
  }
  g.putImageData(img, 0, 0);
  return c;
}

/** Рваные облака перед луной: тёмное тело и холодная кромка сверху. */
export function cloudSheet(w: number, h: number, seed: number, cover = 0.42): Canvas {
  const c = makeCanvas(w, h);
  const g = ctx2d(c);
  const img = g.createImageData(w, h);
  const n = fbm(w, h, [64, 28, 12, 5], seed);
  const dens = (x: number, y: number): number => {
    const yy = Math.min(h - 1, Math.max(0, y));
    const band = Math.sin((yy / h) * Math.PI); // гуще в середине полосы
    return n[yy * w + ((x + w) % w)] * (0.55 + 0.6 * band) - cover;
  };
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const d = dens(x, y);
      if (d <= bayer(x, y) * 0.16) continue;
      const above = dens(x, y - 2);
      const i = (y * w + x) * 4;
      // кромка: сверху плотность ниже → край, подсвеченный луной
      const rim = above < 0.03;
      const body = Math.min(1, d * 3.2);
      const r = rim ? 112 : 18 + body * 12;
      const gg = rim ? 124 : 20 + body * 14;
      const b = rim ? 168 : 40 + body * 22;
      img.data[i] = r;
      img.data[i + 1] = gg;
      img.data[i + 2] = b;
      img.data[i + 3] = 255;
    }
  }
  g.putImageData(img, 0, 0);
  return c;
}

/** Дальний лес на холмах — силуэт, чуть светлее земли. */
export function hillsStrip(w: number, h: number, seed: number): Canvas {
  const c = makeCanvas(w, h);
  const g = ctx2d(c);
  const r = rng(seed);
  const ridge = new Float32Array(w);
  const p1 = r() * 6;
  const p2 = r() * 6;
  for (let x = 0; x < w; x++) {
    const u = (x / w) * Math.PI * 2;
    ridge[x] = h * 0.55 + Math.sin(u * 2 + p1) * h * 0.12 + Math.sin(u * 5 + p2) * h * 0.06;
  }
  g.fillStyle = "rgb(16,15,34)";
  for (let x = 0; x < w; x++) g.fillRect(x, Math.round(ridge[x]), 1, h);
  // ёлки и голые деревья по гребню
  for (let k = 0; k < w / 3; k++) {
    const x = Math.floor(r() * w);
    const base = Math.round(ridge[x]);
    const th = 3 + Math.floor(r() * 9);
    const spiky = r() < 0.7;
    for (let y = 0; y < th; y++) {
      const half = spiky ? Math.floor((y / th) * 2.4) : y === th - 1 ? 0 : 0;
      g.fillRect(x - half, base - th + y, half * 2 + 1, 1);
    }
  }
  return c;
}

// ─── Варианты картинок ────────────────────────────────────────────────────

type Px = (r: number, g: number, b: number, a: number) => [number, number, number, number];

export function remap(src: CanvasImageSource & { width: number; height: number }, f: Px): Canvas {
  const c = makeCanvas(src.width, src.height);
  const g = ctx2d(c);
  g.drawImage(src, 0, 0);
  const img = g.getImageData(0, 0, c.width, c.height);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    if (d[i + 3] === 0) continue;
    const o = f(d[i], d[i + 1], d[i + 2], d[i + 3]);
    d[i] = o[0];
    d[i + 1] = o[1];
    d[i + 2] = o[2];
    d[i + 3] = o[3];
  }
  g.putImageData(img, 0, 0);
  return c;
}

/** Освещение молнией: ярче, контрастнее, в холодный белый. */
export function litVariant(src: CanvasImageSource & { width: number; height: number }, gain = 1.9): Canvas {
  return remap(src, (r, g, b, a) => {
    const l = 0.3 * r + 0.59 * g + 0.11 * b;
    const k = Math.min(1, (l / 255) * gain + 0.12);
    // уровни в 6 ступеней — чтобы свет «лёг» пиксельно, а не градиентом
    const q = Math.round(k * 6) / 6;
    return [Math.min(255, 60 + q * 200), Math.min(255, 70 + q * 190), Math.min(255, 100 + q * 155), a];
  });
}

/** Чёрный силуэт (контровая вспышка). */
export function silhouette(src: CanvasImageSource & { width: number; height: number }, rgb: [number, number, number] = [6, 6, 14]): Canvas {
  return remap(src, (_r, _g, _b, a) => [rgb[0], rgb[1], rgb[2], a]);
}

/** Тонировка (окна в красный на финальном ударе, хроматические двойники логотипа). */
export function tint(src: CanvasImageSource & { width: number; height: number }, m: [number, number, number], add: [number, number, number] = [0, 0, 0]): Canvas {
  return remap(src, (r, g, b, a) => [
    Math.min(255, r * m[0] + add[0]),
    Math.min(255, g * m[1] + add[1]),
    Math.min(255, b * m[2] + add[2]),
    a,
  ]);
}

/** Маска «углей»: только красные трещины логотипа (для пульсации свечения). */
export function emberMask(src: CanvasImageSource & { width: number; height: number }): Canvas {
  return remap(src, (r, g, b, a) => {
    const hot = r > 140 && r > g * 1.7 && r > b * 1.6;
    return hot ? [255, Math.min(255, g + 40), Math.min(255, b + 20), a] : [0, 0, 0, 0];
  });
}

// ─── Окна дома ────────────────────────────────────────────────────────────

export interface WindowRect {
  x: number;
  y: number;
  w: number;
  h: number;
  cx: number;
  cy: number;
  area: number;
}

/** Окна = связные области слоя окон (поиск в ширину по альфе). */
export function findWindows(src: CanvasImageSource & { width: number; height: number }): WindowRect[] {
  const w = src.width;
  const h = src.height;
  const c = makeCanvas(w, h);
  const g = ctx2d(c);
  g.drawImage(src, 0, 0);
  const a = g.getImageData(0, 0, w, h).data;
  const seen = new Uint8Array(w * h);
  const out: WindowRect[] = [];
  const stack: number[] = [];
  for (let i = 0; i < w * h; i++) {
    if (seen[i] || a[i * 4 + 3] === 0) continue;
    let x0 = w, y0 = h, x1 = 0, y1 = 0, n = 0;
    stack.push(i);
    seen[i] = 1;
    while (stack.length) {
      const j = stack.pop() as number;
      const x = j % w;
      const y = (j / w) | 0;
      n++;
      if (x < x0) x0 = x;
      if (x > x1) x1 = x;
      if (y < y0) y0 = y;
      if (y > y1) y1 = y;
      // 8-связность с зазором в 1 пиксель: переплёт не делит окно на части
      for (let dy = -2; dy <= 2; dy++) {
        for (let dx = -2; dx <= 2; dx++) {
          const xx = x + dx;
          const yy = y + dy;
          if (xx < 0 || yy < 0 || xx >= w || yy >= h) continue;
          const k = yy * w + xx;
          if (!seen[k] && a[k * 4 + 3] !== 0) {
            seen[k] = 1;
            stack.push(k);
          }
        }
      }
    }
    if (n >= 3) {
      out.push({ x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1, cx: (x0 + x1) / 2, cy: (y0 + y1) / 2, area: n });
    }
  }
  return out;
}

// ─── Вороны ───────────────────────────────────────────────────────────────

/** Три кадра взмаха (11×6), «#» — перо. */
const RAVEN_FRAMES = [
  [
    "#.........#",
    "##.......##",
    ".##..#..##.",
    "..#######..",
    "....###....",
    ".....#.....",
  ],
  [
    "...........",
    "...........",
    "#####.#####",
    "..#######..",
    "....###....",
    ".....#.....",
  ],
  [
    "...........",
    "...........",
    "....###....",
    "..#######..",
    ".##.###.##.",
    "##...#...##",
  ],
];

export function ravenSprites(rgb: [number, number, number], rim: [number, number, number]): Canvas[] {
  return RAVEN_FRAMES.map((rows) => {
    const c = makeCanvas(rows[0].length, rows.length);
    const g = ctx2d(c);
    const at = (x: number, y: number): boolean => y >= 0 && y < rows.length && x >= 0 && x < rows[y].length && rows[y][x] === "#";
    rows.forEach((row, y) => {
      for (let x = 0; x < row.length; x++) {
        if (!at(x, y)) continue;
        // верхняя кромка перьев — лунный отблеск, иначе ворон тонет в ночи
        const lit = !at(x, y - 1);
        const col = lit ? rim : rgb;
        g.fillStyle = `rgb(${col[0]},${col[1]},${col[2]})`;
        g.fillRect(x, y, 1, 1);
      }
    });
    return c;
  });
}

// ─── Молния ───────────────────────────────────────────────────────────────

export interface Bolt {
  /** Отрезки в «единицах неба»: x — смещение от точки удара, y — 0 вверху, 1 у земли. */
  segs: Array<[number, number, number, number, number]>; // x0,y0,x1,y1,уровень ветки
}

export function makeBolt(seed: number): Bolt {
  const r = rng(seed);
  const segs: Bolt["segs"] = [];
  const walk = (x: number, y: number, len: number, dir: number, depth: number): void => {
    let cx = x;
    let cy = y;
    const steps = Math.max(4, Math.round(len * 26));
    for (let i = 0; i < steps; i++) {
      const ny = cy + len / steps;
      const nx = cx + (r() - 0.5) * 0.11 + dir * 0.02;
      segs.push([cx, cy, nx, ny, depth]);
      if (depth < 2 && r() < (depth === 0 ? 0.13 : 0.06) && ny < 0.85) {
        walk(nx, ny, len * (0.25 + r() * 0.3), r() < 0.5 ? -1.4 : 1.4, depth + 1);
      }
      cx = nx;
      cy = ny;
      if (cy >= 1) break;
    }
  };
  walk((r() - 0.5) * 0.2, 0, 1, 0, 0);
  return { segs };
}

/** Пиксельная линия (Брезенхэм) — без сглаживания канваса. */
export function pixelLine(g: CanvasRenderingContext2D, x0: number, y0: number, x1: number, y1: number, size = 1): void {
  x0 = Math.round(x0);
  y0 = Math.round(y0);
  x1 = Math.round(x1);
  y1 = Math.round(y1);
  const dx = Math.abs(x1 - x0);
  const dy = -Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;
  let guard = 0;
  for (;;) {
    g.fillRect(x0, y0, size, size);
    if ((x0 === x1 && y0 === y1) || ++guard > 2000) break;
    const e2 = 2 * err;
    if (e2 >= dy) {
      err += dy;
      x0 += sx;
    }
    if (e2 <= dx) {
      err += dx;
      y0 += sy;
    }
  }
}
